import { LazyExoticComponent, NamedExoticComponent } from "react";
import { Rule } from "./router.rule";

// Core Types
export type RuleResult = {
  allowed: boolean;
  failureType?: failureTypeEnum;
  failedValue?: string;
  fallback?: FallbackResult;
};

export type FallbackResult = {
  url: string;
  message?: string;
};

export type FallbackFunction = (userData: UserDataType) => FallbackResult;

export type RouteAccessResult = {
  isAllowed: boolean;
  fallbackUrl?: string;
  redirectingMessage?: string;
};

export enum failureTypeEnum {
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
}

// User Role and Authorization Enums
export enum UserRoleList {
  CREATOR_ADMIN = "creator_admin",
  FIREBASE_AUTH = "firebase_auth",
  CREATOR = "creator",
}

// NOTE: Authorizations consists of milestones, flags and feature-flags.
export enum UserAuthorizationList {
  // Flags Authorizations
  CREATOR_WAITLISTED = "is_creator_waitlisted",
  CREATOR_SOURCING_ENABLED = "is_sourcing_enabled",
  CREATOR_BRAND_SOURCING_ENABLED = "is_brand_sourcing_enabled",
  CREATOR_INSTAGRAM_LOGIN_COMPLETE = "is_connected_insta",
  CREATOR_YOUTUBE_LOGIN_COMPLETE = "is_connected_yt",
  CREATOR_TELEGRAM_LOGIN_COMPLETE = "is_connected_telegram",
  CREATOR_FACEBOOK_LOGIN_COMPLETE = "is_connected_facebook",
  CREATOR_BLOCKED = "is_creator_blocked",
  CREATOR_KYC_COMPLETE = "kyc_complete",
  ENGAGE_ACTIVE = "is_engage_active",
  ENGAGE_FACEBOOK_ACTIVE = "is_facebook_engage_active",
  CREATOR_SOCIAL_LOGIN_ACTIVE = "is_social_login_active",
  CREATOR_SOCIAL_ACCOUNT_TYPE = "is_social_account_type",
  CREATOR_NSL = "is_nsl_creator",
  CREATOR_REPEAT = "is_repeat_creator",

  // Milestones Authorizations
  SOCIAL_LOGIN_MILESTONE_COMPLETE = "SOCIAL_LOGIN",
  ENGAGE_CONNECTED = "ENENGAGE_CONNECTED",

  // Feature flags authorizations
  BRAND_COLLAB__COLLAB_LIVE = "BRAND_COLLAB__COLLAB_LIVE",
  BRAND_COLLAB__COLLAB_COMING_SOON = "BRAND_COLLAB__COLLAB_COMING_SOON",
  BRAND_COLLAB__COLLAB_CHAT_LIVE = "COLLAB_CHAT__COLLAB_CHAT_LIVE",
  COLLAGE_V1__INTERNAL_CREATORS = "COLLAGE_V1__INTERNAL_CREATORS",
  COLLAGE_V1__COLLAGE_LIVE = "COLLAGE_V1__COLLAGE_LIVE",
  GROWTH_V2__PRODUCT_GROWTH = "GROWTH_V2__PRODUCT_GROWTH",
  REWARDS__REWARDS_LIVE = "REWARDS__REWARDS_LIVE",
  AUTO_CONTENT__AUTO_CONTENT_ENABLED = "AUTO_CONTENT__AUTO_CONTENT_ENABLED",
  NEW_SOCIAL_LOGIN_EDUCATION__SOCIAL_LOGIN_EDUCATION_REQUIRED = "NEW_SOCIAL_LOGIN_EDUCATION__SOCIAL_LOGIN_EDUCATION_REQUIRED",
  ENGAGE_SERVICE__ENGAGE_VIA_FACEBOOK = "ENGAGE_SERVICE__ENGAGE_VIA_FACEBOOK",
  REFERRAL_PROGRAM__REFERRAL_DISABLED = "REFERRAL_PROGRAM__REFERRAL_DISABLED",
  RTC__MAINTENANCE = "RTC__MAINTENANCE",
  RTC__REFRESH = "RTC__REFRESH",
  RTC_SELECTIVE__LIVE = "RTC_SELECTIVE__LIVE",
  RTC_FINAL_APPROVAL__BLOCKED = "RTC_FINAL_APPROVAL__BLOCKED",
  MEESHO_QR_CODE__QR_ENABLED = "MEESHO_QR_CODE__QR_ENABLED",
  MEESHO_DISPUTE__DISPUTE_ENABLED = "MEESHO_DISPUTE__DISPUTE_ENABLED",
  FACEBOOK_SERVICE__CHANNEL_ENABLED = "FACEBOOK_SERVICE__CHANNEL_ENABLED",
  CROSS_POSTING__POSTING_ENABLED = "CROSS_POSTING__POSTING_ENABLED",
  FIRST_POST = "FIRST_POST",
  SELECT_BY_WISHLINK__SELECT_BY_WISHLINK_LIVE = "SELECT_BY_WISHLINK__SELECT_BY_WISHLINK_LIVE",
  WISHLINK_WRAPPED__WISHLINK_WRAPPED_2025 = "WISHLINK_WRAPPED__WISHLINK_WRAPPED_2025",
  AUTO_SCRAPING__ENABLED = "AUTO_SCRAPING__ENABLED",
  CUSTOM_ENGAGE__ENABLED = "CUSTOM_ENGAGE__ENABLED",
  BULK_LINKS__ENABLED = "BULK_LINKS__ENABLED",
  ENGAGE_V2__ENABLED = "ENGAGE_V2__ENABLED",
  LIBAS_BANNER__ENABLED = "LIBAS_BANNER__ENABLED",
  SHARE_TO_WISHLINK__ENABLED = "SHARE_TO_WISHLINK__ENABLED",
  BRANDS_HUB_V2__ENABLED = "BRANDS_HUB_V2__ENABLED",
  STREAKS_V2__ENABLED = "STREAKS_V2__enabled",
  IN_APP_BROWSER__ENABLED = "IN_APP_BROWSER__ENABLED",
  BRAND_REWARD_NEWME__ENABLED = "BRAND_REWARD_NEWME__ENABLED",
  BRAND_REWARD_OFFDUTY__ENABLED = "BRAND_REWARD_OFFDUTY__ENABLED",
  BRAND_REWARD_SWISSBEAUTY__ENABLED = "BRAND_REWARD_SWISSBEAUTY__ENABLED",
  BRAND_REWARD_TIGC__ENABLED = "BRAND_REWARD_TIGC__ENABLED",
  BRAND_REWARD_PANTALOONS__ENABLED = "BRAND_REWARD_PANTALOONS__ENABLED",
  BRAND_REWARD_BEWAKOOF__ENABLED = "BRAND_REWARD_BEWAKOOF__ENABLED",
  BRAND_REWARD_FIRSTCRY__ENABLED = "BRAND_REWARD_FIRSTCRY__ENABLED",
  BRAND_REWARD_URBANO__ENABLED = "BRAND_REWARD_URBANO__ENABLED",
  BRAND_REWARD_FABLESTREET__ENABLED = "BRAND_REWARD_FABLESTREET__ENABLED",
  BRAND_REWARD_LAZOSTORE__ENABLED = "BRAND_REWARD_LAZOSTORE__ENABLED",
  WISHII_CREATOR_AGENT__ENABLED = "WISHII_CREATOR_AGENT__ENABLED",
  ENGAGE_COMMENT_REPLIER__ENABLED = "ENGAGE_COMMENT_REPLIER__ENABLED",
  PAYOUT_MAINTENANCE__ENABLED = "PAYOUT_MAINTENANCE__ENABLED",
  IG_POST_SUGGESTION__ENABLED = "IG_POST_SUGGESTION__ENABLED",
  MEESHO_EXTENDED_COMMISSION_RANGE__ENABLED = "MEESHO_EXTENDED_COMMISSION_RANGE__ENABLED",

  // FTUX Authorizations
  FTUX_BRAND_COLLAB = "FTUX_BRAND_COLLAB",
  FTUX_GROWTH_V2 = "FTUX_GROWTH_V2",
  FTUX_REWARDS = "FTUX_REWARDS",
  FTUX_SOURCING = "FTUX_SOURCING",
  FTUX_ENGAGE = "FTUX_ENGAGE",
  FTUX_COLLAB_CHAT = "FTUX_COLLAB_CHAT",
}

// User and Route Types
export type UserDataType = {
  auth_level: UserRoleList;
  authorizations: {
    [key in UserAuthorizationList]: {
      achieved: boolean;
    };
  };
};

type ComponentResolver = (
  userData: UserDataType
) =>
  | React.JSX.Element
  | LazyExoticComponent<() => React.JSX.Element>
  | LazyExoticComponent<NamedExoticComponent<object>>;

export type AppRoute = {
  /**
   * @author: @BhaveshSonalkar
   * @date: 2025-04-09
   * @comments: Any changes to this type must be approved by the approvers below.
   * @approvers: @BhaveshSonalkar, @ChandanWishlink
   */
  path: string;
  access: Rule;
  component:
    | React.JSX.Element
    | LazyExoticComponent<() => React.JSX.Element>
    | LazyExoticComponent<NamedExoticComponent<object>>
    | ComponentResolver;
  customFallback?: FallbackFunction;
  showFooter: boolean;
  permanentlyMovedTo?: string;
  props?: Record<string, any>;
};

export type validateRouteAccessType = ({
  userData,
  route,
  customFallback,
}: {
  userData: UserDataType;
  route: AppRoute;
  customFallback?: FallbackFunction;
}) => RouteAccessResult;

export type evaluatorType = (userData: UserDataType) => {
  allowed: boolean;
  failureType?: failureTypeEnum;
  failedValue?: string;
  isBlocked?: boolean;
};
